import { Component, Element, Host, Prop, h } from '@stencil/core';
import { Consumer } from '@sydle/ui-utils';
import { BlogPost } from '../../../types/blog';
import { type SocialNetwork } from './share-bar.types';
import { contentShareEvent } from '../../../utils/tracking-utils';

@Component({
  tag: 'sy-blog-share-bar',
  styleUrl: 'scss/index.scss',
  shadow: true,
})
export class SyBlogShareBar {
  private consumer: Consumer;
  @Element() el: HTMLSyBlogShareBarElement;

  /**
   * Post body
   */
  @Prop() postBody: BlogPost;

  /**
   * List of social network configurations for sharing buttons.
   * Each configuration should include properties like 'name', 'sharingMessage', 'templateURL', etc.
   */
  @Prop() socialNetworkConfig: SocialNetwork[];

  /**
   * Sets the direction of the share bar.
   */
  @Prop({ reflect: true }) direction: 'row' | 'column' = 'row';

  /**
   * Share bar title
   */
  @Prop() shareBarTitle: string;

  /**
   * Builds the share URL using the provided message, template, and share URL.
   * @param message The share message.
   * @param template The URL template.
   * @param shareUrl The share URL.
   */
  private buildShareUrl(message: string, template: string, shareUrl: string) {
    if (template) {
      return template
        .replace('{{message}}', message)
        .replace('{{postURL}}', shareUrl.match(/http/) ? shareUrl : window.location.origin + shareUrl)
        .replace('{{title}}', this.postBody.title);
    }
  }

  /**
   * Gets the inline style based on the direction of the share bar.
   */
  private getStyle() {
    const style = this.direction === 'row' ? { width: 'min(90vw, 75ch)', margin: 'auto' } : {};
    Object.assign(style, { '--share-bar-direction': this.direction });
    return style;
  }

  connectedCallback() {
    this.consumer = new Consumer(this.el);
    this.consumer.subscribe('postBody', (post: BlogPost) => (this.postBody = post));
  }

  disconnectedCallback() {
    this.consumer.disconnect();
  }

  render() {
    if (!this.postBody) return null;
    return (
      <Host style={this.getStyle()}>
        <div id={`share-${this.direction}`}>
          <sy-text type={this.direction === 'row' ? 'h4' : 'small'} class="sy-mb-2">
            {this.shareBarTitle}
          </sy-text>
          <div id="share-buttons">
            {this.socialNetworkConfig?.map((media) => {
              return (
                <a
                  key={media.name}
                  title={media.name}
                  href={this.buildShareUrl(
                    media.sharingMessage,
                    media.templateURL,
                    this.postBody.urls.find((url) => url.rel === 'canonical')?.href || '',
                  )}
                  onClick={() => {
                    contentShareEvent(this.postBody, media.name.toLowerCase());
                  }}
                  rel="external noreferrer"
                  target="_blank"
                >
                  <sy-icon class="sy-btn-icon" role="img" name={media.icon}></sy-icon>
                </a>
              );
            })}
          </div>
        </div>
      </Host>
    );
  }
}
